import { useCallback, useEffect } from 'react'
import { useSocketConnection } from './useSocketConnection'
import { IAIVideoSocketMessage } from 'types/aiVideo'
import { IAIImageSocketMessage } from 'types/aiImage'
import { IUserCreditsSocketMessage } from 'types/userCredits'
import { IAIAvatarSocketMessage } from 'services/socket/aiAvatar/types'
import {
  handleAiVideoSocketData,
  handleAiVideoPastSocketData
} from 'services/socket/aiVideo'
import { handleAIImgSocketData } from 'services/socket/aiImage'
import { handleUserCreditsSocketData } from 'services/socket/userCredits'
import {
  handleAiAvatarSocketData,
  handleAiAvatarPastSocketData
} from 'services/socket/aiAvatar'
import { ISocketStateRecover } from '../types/socketStateRecover'

export const useAppSockets = () => {
  const enableSocketConnection = useSocketConnection()

  const handleSocketDataByType = useCallback(
    (
      data:
        | IAIVideoSocketMessage
        | IAIImageSocketMessage
        | IUserCreditsSocketMessage
        | IAIAvatarSocketMessage
    ) => {
      switch (data.type) {
        case 'ai-video':
          handleAiVideoSocketData(data)
          break
        case 'ai-image':
          handleAIImgSocketData(data)
          break
        case 'user-credits':
          handleUserCreditsSocketData(data)
          break
        case 'ai-avatar':
          handleAiAvatarSocketData(data)
          break
      }
    },
    []
  )

  const handlePastSocketDataByType = useCallback(
    (
      data: ISocketStateRecover<
        | IAIVideoSocketMessage
        | IAIImageSocketMessage
        | IUserCreditsSocketMessage
        | IAIAvatarSocketMessage
      >
    ) => {
      const pastSocketsEventsData = data?.pastSocketsEventsData
      pastSocketsEventsData?.forEach(eventData => {
        if (
          eventData.type === 'ai-video' ||
          'gen_ai_video_hash' in eventData ||
          'ai_video_generation_hash' in eventData
        ) {
          handleAiVideoPastSocketData(eventData)
        } else if (
          eventData.type === 'ai-avatar' ||
          'ai_avatar_video_hash' in eventData
        ) {
          // The `in` fallback widens the inferred type (past events may arrive
          // without the `type` discriminant); the runtime condition guarantees
          // an avatar message, and the handler re-guards on the hash.
          handleAiAvatarPastSocketData(eventData as IAIAvatarSocketMessage)
        }
      })
    },
    []
  )

  useEffect(() => {
    window.updateProgressBar = (
      data:
        | ISocketStateRecover<
            | IAIVideoSocketMessage
            | IAIImageSocketMessage
            | IUserCreditsSocketMessage
            | IAIAvatarSocketMessage
          >
        | IAIVideoSocketMessage
        | IAIImageSocketMessage
        | IUserCreditsSocketMessage
        | IAIAvatarSocketMessage
    ) => {
      if ('type' in data) {
        handleSocketDataByType(data)
      } else if (
        'gen_ai_video_hash' in data ||
        'ai_video_generation_hash' in data
      ) {
        handleAiVideoSocketData(data)
      }

      if ('socketType' in data) handlePastSocketDataByType(data)
    }
  }, [handleSocketDataByType, handlePastSocketDataByType])

  useEffect(() => {
    enableSocketConnection()
  }, [enableSocketConnection])
}
